/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import usePaginationHook from "hooks/usePagination";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import {
  deleteStore,
  getStore,
  updateStoreStatus,
} from "services/storeService";
import sessionHelper from "services/sessionHelper";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import { storeItemListColumn } from "utils/tableHelper/storeTableHelper";
import useStyles from "styles/store/storeEditor";
import { sortArray } from "utils/miscUtils";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";

interface RowActionsProps {
  data: any;
  actionHandlers: {
    handleDisable?: (id: any) => void;
    handleDelete?: (data: any) => void;
  };
}

const RowActions = ({
  data,
  actionHandlers: { handleDisable = () => {}, handleDelete = () => {} },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(configData.routes.store.edit?.replace(":id", data?._id));
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.STORE.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        <MenuItem onClick={() => handleDisable(data?._id)}>
          {data?.isDisabled ? "Enable" : "Disable"}
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => handleDelete(data)}>Delete</MenuItem>
        )}
      </Menu>
    </>
  );
};
const Store = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.STORE.UPDATE_ROLES
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (v?: any) => {
    setLoading(true);
    try {
      const resp = await getStore({
        pageNo: pageNo,
        pageSize: pageSize,
      });
      setFilteredList(
        resp?.items.map((el) => {
          let keys = Object.keys(el.buyPrice);
          return {
            ...el,
            isDisabledText: el?.isDisabled ? "Yes" : "No",
            rowDisabled: el?.isDisabled ?? false,
            buyPrice: JSON.stringify(
              keys.map((key, index) => {
                let _row = `${key}= ${el?.buyPrice[key]}`;
                return index === keys.length - 1 ? _row : _row + ", ";
              })
            ),
          };
        })
      );
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const createPlatform = () => navigate(configData.routes.store.create);

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?._id, id: data?._id });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteStore(constentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onDisable = async (storeId) => {
    try {
      const resp = await updateStoreStatus(storeId);
      showSnackbar(resp?.data?.message);
      fetchData();
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    const initialColumns = storeItemListColumn().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
  }, [pageNo, pageSize, hasEditPermission]);

  return (
    <ContentShell
      title="Store Coins"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          {sessionHelper.hasRole(PermittedRoles.STORE.CREATE_ROLES) && (
            <CustomButton onClick={createPlatform}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          actions={RowActions}
          pageSize={pageSize}
          totalEntries={filteredList?.length}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          highlightAltRow={true}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            handleDisable: onDisable,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default Store;
